import { useNavigation } from "@react-navigation/native";
import { CheckCircle, Plus } from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";
import { View, XStack, YStack } from "tamagui";
import { useApi } from "../../../core/apis";
import { useStores } from "../../../core/stores";
import { Permission } from "../../../permissions/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Button } from "../../../ui/Button";
import { Group } from "../../../ui/Group";
import { Label } from "../../../ui/Label";
import { FullScreenLoading } from "../../../ui/Loading";
import type { BusinessPlanModel } from "../../plan/BusinessPlanModel";
import { BusinessModuleBlockRenderer } from "../BusinessModuleUIRenderer";
import { BusinessModuleType } from "../types";
import { BusinessAddCardSheet } from "./BusinessAddCardSheet";

export function useLoadBusinessPlanModules(
  businessPlanModel?: BusinessPlanModel,
) {
  const { businessModulesApi } = useApi();
  const { businessModulesStore } = useStores();

  React.useEffect(() => {
    businessPlanModel?.data.eventInfo.tasks.forEach(({ businessModuleUid }) => {
      if (!businessModulesStore.byId.get(businessModuleUid)) {
        businessModulesApi.getById(businessModuleUid);
      }
    });
  }, [businessPlanModel, businessModulesApi, businessModulesStore]);

  return (
    !!businessPlanModel &&
    businessPlanModel.businessModules.length ===
      businessPlanModel.data.eventInfo.tasks.filter((t) => t.isVisible).length
  );
}

export const BusinessTasksList = observer(
  ({ businessPlan }: { businessPlan: BusinessPlanModel }) => {
    const { t } = useTranslation();
    const navigation = useNavigation();

    const isModulesLoaded = useLoadBusinessPlanModules(businessPlan);

    const [showHidden, setShowHidden] = React.useState(false);
    const [showAddStep, setShowAddStep] = React.useState(false);

    const canEdit = businessPlan.checkPermission(Permission.CanEditModules);

    if (!isModulesLoaded) {
      return <FullScreenLoading />;
    }

    const completedModules = businessPlan.completedBusinessModules;

    return (
      <View>
        <Box>
          <XStack gap={"$medium"}>
            <View width={32} flexShrink={0}>
              <CheckCircle />
            </View>
            <YStack flex={1} gap={"$small"}>
              <Label>
                {t("business:tasks_progress_summary", {
                  completed: businessPlan.completedTasksCount,
                  total: businessPlan.totalTasksCount,
                  percentage: Math.round(
                    100 *
                      (businessPlan.completedTasksCount /
                        businessPlan.totalTasksCount),
                  ),
                })}
              </Label>
              {!!completedModules.length && (
                <Body
                  cursor={"pointer"}
                  onPress={() => {
                    setShowHidden(!showHidden);
                  }}
                  muted
                  textDecorationLine={"underline"}
                >
                  {t("business:show_completed_tasks", {
                    count: completedModules.length,
                  })}
                </Body>
              )}
            </YStack>
          </XStack>
          {showHidden && (
            <Group>
              {completedModules?.map((module) => (
                <BusinessModuleBlockRenderer
                  key={module.uid}
                  uid={module.uid}
                  active={true}
                  upcoming={false}
                  onPress={() => {
                    navigation.navigate("BusinessTasksModule", {
                      businessUid: businessPlan.uid,
                      businessModuleUid: module.uid,
                    });
                  }}
                />
              ))}
            </Group>
          )}
          <Group>
            {businessPlan.notCompletedBusinessModules?.map((module) => (
              <BusinessModuleBlockRenderer
                key={module.uid}
                uid={module.uid}
                active={true}
                upcoming={false}
                onPress={() => {
                  navigation.navigate("BusinessTasksModule", {
                    businessUid: businessPlan.uid,
                    businessModuleUid: module.uid,
                  });
                }}
              />
            ))}
          </Group>
        </Box>

        {canEdit && (
          <>
            <Button
              icon={Plus}
              type={"secondary"}
              onPress={() => {
                setShowAddStep(true);
              }}
            >
              {t("business:add_task")}
            </Button>
            <BusinessAddCardSheet
              overrideTypes={[BusinessModuleType.Reminder]}
              onOpenChange={() => setShowAddStep(false)}
              open={showAddStep}
              sheetId={"business-step-add"}
              onSave={async (moduleData) => {
                const m = await businessPlan.addBusinessModule(moduleData);
                return businessPlan.addEventModule(m.uid);
              }}
            />
          </>
        )}
      </View>
    );
  },
);
